import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api, type OccasionRow } from '../api.js';
import { Button, Card, ErrorBox, Loading, Screen } from '../ui.js';

/**
 * D-34: Anlässe anlegen, umbenennen, in der Zielformalität ändern, entfernen.
 *
 * Die Zielformalität ist der Grund, warum ein Anlass mehr ist als ein Wort: sie steuert,
 * wie förmlich die Vorschläge ausfallen (§6 Schritt 3). Deshalb steht sie hier direkt
 * neben dem Namen und nicht in einer entlegenen Einstellung.
 */
const FORMALITAET: { wert: number; text: string }[] = [
  { wert: 1, text: '1 — sehr leger' },
  { wert: 2, text: '2 — leger' },
  { wert: 3, text: '3 — normal' },
  { wert: 4, text: '4 — förmlich' },
  { wert: 5, text: '5 — sehr förmlich' },
];

export function Occasions({ navigate }: { navigate: (to: string) => void }): ReactNode {
  const qc = useQueryClient();
  const [zeigeArchivierte, setZeigeArchivierte] = useState(false);
  const [neuerName, setNeuerName] = useState('');
  const [neueFormalitaet, setNeueFormalitaet] = useState(3);
  const [meldung, setMeldung] = useState<string | null>(null);

  const anlaesse = useQuery({
    queryKey: ['occasions', zeigeArchivierte],
    queryFn: () => api.occasions.list(zeigeArchivierte),
  });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['occasions'] });
    // Die Vorschläge hängen an der Zielformalität des Anlasses.
    void qc.invalidateQueries({ queryKey: ['recommend'] });
  };

  const anlegen = useMutation({
    mutationFn: () => api.occasions.create(neuerName.trim(), neueFormalitaet),
    onSuccess: () => {
      setNeuerName('');
      setNeueFormalitaet(3);
      setMeldung(null);
      invalidate();
    },
  });

  const aendern = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.occasions.update(id, patch),
    onSuccess: invalidate,
  });

  const entfernen = useMutation({
    mutationFn: (id: string) => api.occasions.remove(id),
    onSuccess: (ergebnis) => {
      setMeldung(
        ergebnis.archiviert
          ? `Archiviert statt gelöscht: ${ergebnis.verwendungen} Protokolle verweisen darauf. ` +
            'Die Vergangenheit bleibt damit vollständig.'
          : 'Gelöscht.',
      );
      invalidate();
    },
  });

  if (anlaesse.isLoading) return <Loading />;
  const liste = anlaesse.data?.occasions ?? [];

  return (
    <Screen
      title="Anlässe"
      subtitle="Bestimmen, wie förmlich die Vorschläge ausfallen"
      action={
        <Button variant="ghost" onClick={() => navigate('/mehr')}>
          Zurück
        </Button>
      }
    >
      <ErrorBox error={anlaesse.error ?? anlegen.error ?? aendern.error ?? entfernen.error} />
      {meldung !== null && (
        <p className="mb-4 rounded-[var(--radius-card)] bg-white/6 p-3 text-[13px] text-white/70">
          {meldung}
        </p>
      )}

      <Card className="mb-4">
        <p className="text-[15px] font-semibold">Neuer Anlass</p>
        <input
          value={neuerName}
          onChange={(e) => setNeuerName(e.target.value)}
          placeholder="z. B. Elternabend"
          className="mt-3 w-full rounded-[var(--radius-control)] bg-white/6 px-3.5 py-3 text-[15px] ring-1 ring-white/10 outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
        />
        <select
          value={neueFormalitaet}
          onChange={(e) => setNeueFormalitaet(Number(e.target.value))}
          className="mt-2 w-full rounded-[var(--radius-control)] bg-white/6 px-3.5 py-3 text-[15px] ring-1 ring-white/10 outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
        >
          {FORMALITAET.map((f) => (
            <option key={f.wert} value={f.wert}>
              {f.text}
            </option>
          ))}
        </select>
        <Button
          className="mt-3 w-full"
          disabled={neuerName.trim() === '' || anlegen.isPending}
          onClick={() => anlegen.mutate()}
        >
          Anlegen
        </Button>
      </Card>

      <div className="space-y-3">
        {liste.map((anlass) => (
          <AnlassKarte
            key={anlass.id}
            anlass={anlass}
            busy={aendern.isPending || entfernen.isPending}
            onAendern={(patch) => aendern.mutate({ id: anlass.id, patch })}
            onEntfernen={() => entfernen.mutate(anlass.id)}
          />
        ))}
      </div>

      <Button
        variant="ghost"
        className="mt-4 w-full"
        onClick={() => setZeigeArchivierte(!zeigeArchivierte)}
      >
        {zeigeArchivierte ? 'Archivierte ausblenden' : 'Archivierte anzeigen'}
      </Button>
      <div className="h-8" />
    </Screen>
  );
}

function AnlassKarte({ anlass, busy, onAendern, onEntfernen }: {
  anlass: OccasionRow;
  busy: boolean;
  onAendern: (patch: Record<string, unknown>) => void;
  onEntfernen: () => void;
}): ReactNode {
  const [name, setName] = useState(anlass.label);
  const [offen, setOffen] = useState(false);

  return (
    <Card className={anlass.archived ? 'opacity-60' : ''}>
      <button className="w-full text-left" onClick={() => setOffen(!offen)}>
        <p className="text-[15px] font-medium">
          {anlass.label}
          {anlass.archived && <span className="ml-2 text-[11px] text-white/40">archiviert</span>}
        </p>
        <p className="mt-0.5 text-[12px] text-white/45">
          Zielformalität {anlass.formality} ·{' '}
          {FORMALITAET.find((f) => f.wert === anlass.formality)?.text.split('— ')[1] ?? ''}
        </p>
      </button>

      {offen && (
        <div className="mt-4 space-y-2 border-t border-white/8 pt-4">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-[var(--radius-control)] bg-white/6 px-3.5 py-3 text-[15px] ring-1 ring-white/10 outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
          />
          <select
            value={anlass.formality}
            onChange={(e) => onAendern({ formality: Number(e.target.value) })}
            className="w-full rounded-[var(--radius-control)] bg-white/6 px-3.5 py-3 text-[15px] ring-1 ring-white/10 outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
          >
            {FORMALITAET.map((f) => (
              <option key={f.wert} value={f.wert}>
                {f.text}
              </option>
            ))}
          </select>

          <div className="flex gap-2">
            <Button
              className="flex-1"
              disabled={busy || name.trim() === '' || name === anlass.label}
              onClick={() => onAendern({ label: name.trim() })}
            >
              Namen speichern
            </Button>
            {anlass.archived ? (
              <Button variant="ghost" disabled={busy} onClick={() => onAendern({ archived: false })}>
                Zurückholen
              </Button>
            ) : (
              <Button variant="danger" disabled={busy} onClick={onEntfernen}>
                Entfernen
              </Button>
            )}
          </div>
          <p className="text-[11px] text-white/35">
            Wird ein Anlass bereits in Protokollen verwendet, wird er archiviert statt
            gelöscht — sonst hätte die Vergangenheit Lücken.
          </p>
        </div>
      )}
    </Card>
  );
}
